/* eslint-disable */
import { useState, useCallback, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import DhdStatesPricing from "./deliveryPrice/dhd";
import {
  createNewApplication,
  getLatestApplication,
  updateApplication,
  resolveDhdStatus,
} from "../utils/dhdHelpers";
import {
  X,
  Handshake,
  Check,
  Rocket,
  FileDown,
  MapPin,
  Star,
  Building2,
  ChevronDown,
} from "lucide-react";

const deliveryOptions = [
  {
    name: "Zrexpress",
    img: "https://play-lh.googleusercontent.com/_JY-6AlfdbrqaZMEosTsMUCnezFQACvr5iGP19U814Hlw0vIhta7GZSBdQcb9JLHBn2MpHdWzCewLeWb6oJ15Q",
  },
  {
    name: "48h",
    img: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTpvBI5915GAHfwbfd0K0cJkj7Ai5uAkVjLDA&s",
  },
  {
    name: "Noest",
    img: "https://tidjara.dz/wp-content/uploads/2025/06/nord-et-ouest-express-e1749588057773.jpg",
  },
  {
    name: "DHD",
    img: "https://play-lh.googleusercontent.com/-NyxDEFHK6VRmzCptix3FdigtIA-6sVZZM3wK69ZXiboYrt5ZOWDmhdy2dpFdOR49MM",
  },
  {
    name: "Yalidine",
    img: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQp3Ik_w48uUJWH5NGSsV4sVAucJ0KJZ4hm_g&s",
  },
  {
    name: "World Express",
    img: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQDjSd_cCwCkhpk7Ekz9S9XfJWkWNvrlFrY8Q&s",
  },
  {
    name: "Guepex",
    img: "guepex_logo.jpeg",
  },
  {
    name: "Maystro",
    img: "https://media.licdn.com/dms/image/v2/C560BAQHYfllwi1IFdA/company-logo_200_200/company-logo_200_200/0/1643802585466/maystro_delivery_logo?e=2147483647&v=beta&t=wyoEZkWgj_XG3Cloo-4g4rWx5fNl68IGj_FcbHU2y_U",
  },
  {
    name: "other-company",
    img: "https://cdn-icons-png.flaticon.com/512/7059/7059579.png",
  },
];

const StepBar = ({ step, total = 3 }) => (
  <div className="flex items-center gap-1.5 w-full max-w-[180px] mx-auto mb-6">
    {Array.from({ length: total }).map((_, i) => {
      const n = i + 1;
      const done = n < step;
      const current = n === step;
      return (
        <div
          key={n}
          className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${
            done || current ? "bg-[#9b59bf]" : "bg-gray-200"
          }`}
        />
      );
    })}
  </div>
);

export default function Dhd({ isOpen, setIsOpen, onComplete }) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";
  const [step, setStep] = useState(1);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [hasDHD, setHasDHD] = useState(false);
  const [saving, setSaving] = useState(false);

  const user = useSelector(
    (state) =>
      state.AccountReducer?.user ||
      state.accountReducer?.user ||
      state.userReducer?.user ||
      null,
  );
  const account = useSelector(
    (state) => state.AccountReducer || state.accountReducer || {},
  );

  const [formData, setFormData] = useState({
    rating: "ممتاز",
    state: "",
    wilaya_code: "",
  });

  const [wilayaOpen, setWilayaOpen] = useState(false);

  const wilayas = Array.isArray(DhdStatesPricing) ? DhdStatesPricing : [];

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);

  const handleToggle = useCallback((name) => {
    setSelectedCompanies((prev) => {
      const next = prev.includes(name)
        ? prev.filter((c) => c !== name)
        : [...prev, name];
      setHasDHD(next.includes("DHD"));
      return next;
    });
  }, []);

  const handleFinish = async () => {
    if (saving) return;
    setSaving(true);

    const uid =
      typeof user === "string" ? user : user?.uid || account?.user || null;

    try {
      if (uid) {
        let app = await getLatestApplication(uid);
        const status = resolveDhdStatus(app);

        if (!app || status === "rejected") {
          app = await createNewApplication(uid, {
            userEmail: account?.email || null,
          });
        }

        await updateApplication(uid, app.id, {
          selectedCompanies,
          rating: formData.rating,
          state: formData.state,
          wilaya_code: formData.wilaya_code,
          acceptDHD: true,
          pdfDownloaded: false,
          finished: false,
          currentStep: 1,
          status: null,
        });
      }
    } catch (e) {
      console.error("Error saving DHD application:", e);
    } finally {
      setSaving(false);
      if (typeof onComplete === "function") {
        onComplete();
      } else {
        handleClose();
      }
    }
  };

  const wilayaBtnRef = useRef(null);

  const updateWilayaMenuPosition = useCallback(() => {
    const el = wilayaBtnRef.current;
    if (!el) return;
  }, []);

  const closeWilaya = () => {
    setWilayaOpen(false);
  };

  useEffect(() => {
    if (!wilayaOpen) return undefined;

    const onDown = (e) => {
      if (
        e.target.closest?.("[data-wilaya-dropdown]") ||
        e.target.closest?.("[data-wilaya-menu]")
      ) {
        return;
      }
      closeWilaya();
    };

    const onReposition = () => updateWilayaMenuPosition();

    document.addEventListener("mousedown", onDown);
    window.addEventListener("resize", onReposition);
    window.addEventListener("scroll", onReposition, true);

    return () => {
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("resize", onReposition);
      window.removeEventListener("scroll", onReposition, true);
    };
  }, [wilayaOpen, updateWilayaMenuPosition]);

  useEffect(() => {
    if (!wilayaOpen) return undefined;
    const onDown = (e) => {
      if (!e.target.closest?.("[data-wilaya-dropdown]")) {
        setWilayaOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [wilayaOpen]);

  // reset wizard when modal re-opens
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setSelectedCompanies([]);
      setHasDHD(false);
      setSaving(false);
      setWilayaOpen(false);
      setFormData({ rating: "ممتاز", state: "", wilaya_code: "" });
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const canGoNext = selectedCompanies.length > 0 && !!formData.state;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-6"
      dir={isRTL ? "rtl" : "ltr"}
    >
      <div
        className="absolute inset-0 bg-[#1a1025]/70 backdrop-blur-[3px]"
        onClick={handleClose}
      />

      <div
        className="relative w-full sm:max-w-2xl bg-white sm:rounded-[28px] rounded-t-[28px] overflow-hidden shadow-2xl flex flex-col max-h-[92vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative shrink-0 overflow-hidden bg-gradient-to-br from-[#9b59bf] to-[#be97d4] px-5 pt-5 pb-6">
          <div className="absolute inset-0 opacity-30 pointer-events-none">
            <div className="absolute -top-10 -end-10 w-40 h-40 rounded-full bg-white/20 blur-2xl" />
            <div className="absolute bottom-0 start-0 w-32 h-32 rounded-full bg-fuchsia-300/30 blur-2xl" />
          </div>

          <button
            type="button"
            onClick={handleClose}
            className="absolute top-4 end-4 z-10 w-9 h-9 rounded-full bg-black/25 text-white flex items-center justify-center backdrop-blur-md hover:bg-black/40 transition-colors"
            aria-label={t("video.close")}
          >
            <X size={18} />
          </button>

          <div className="relative z-10">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-white/20 backdrop-blur-md px-3 py-1 text-[11px] font-medium text-white mb-2 border border-white/20">
              <Handshake size={12} />
              {t("systemDhd.header.contest")}
            </div>
            <h2 className="text-white text-xl sm:text-2xl font-bold leading-tight drop-shadow-sm">
              {t("systemDhd.header.title")}
            </h2>
            <p className="mt-2 text-sm text-white/85 font-medium max-w-[90%]">
              {t("systemDhd.header.badge")}
            </p>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 sm:p-6">
          <StepBar step={step} />

          {/* Step 1 */}
          {step === 1 && (
            <div className="space-y-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-purple-100 text-[#9b59bf] flex items-center justify-center shrink-0">
                  <Building2 size={18} />
                </div>
                <h3 className="text-base font-bold text-gray-900 leading-snug">
                  {t("systemDhd.step1.selectCompanies")}
                </h3>
              </div>

              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2.5">
                {deliveryOptions.map((opt) => {
                  const selected = selectedCompanies.includes(opt.name);
                  return (
                    <button
                      key={opt.name}
                      type="button"
                      onClick={() => handleToggle(opt.name)}
                      className={`relative flex flex-col items-center justify-center p-3 rounded-2xl border-2 transition-all duration-150 active:scale-[0.98] ${
                        selected
                          ? "border-[#9b59bf] bg-purple-50 shadow-sm shadow-purple-500/15"
                          : "border-gray-100 bg-gray-50/80 hover:border-purple-200"
                      }`}
                    >
                      {selected && (
                        <div className="absolute -top-1.5 -end-1.5 w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center shadow-sm">
                          <Check size={11} strokeWidth={3} />
                        </div>
                      )}
                      <div
                        className={`w-11 h-11 mb-2 overflow-hidden rounded-full border-2 ${
                          selected ? "border-purple-300" : "border-gray-200"
                        }`}
                      >
                        <img
                          src={opt.img}
                          alt={opt.name}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      </div>
                      <span
                        className={`text-[11px] font-bold text-center leading-tight ${
                          selected ? "text-[#9b59bf]" : "text-gray-600"
                        }`}
                      >
                        {opt.name === "other-company"
                          ? t("systemDhd.deliveryOptions.otherCompany")
                          : opt.name}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* Rating pills */}
              <div className="space-y-1.5">
                <label className="flex items-center gap-1.5 text-xs font-bold text-gray-600">
                  <Star size={12} className="text-[#9b59bf]" />
                  {t("systemDhd.step1.rating")}
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    {
                      value: "ممتاز",
                      label: t("systemDhd.step1.ratings.excellent"),
                    },
                    {
                      value: "جيد",
                      label: t("systemDhd.step1.ratings.good"),
                    },
                    {
                      value: "متوسط",
                      label: t("systemDhd.step1.ratings.average"),
                    },
                  ].map((opt) => {
                    const active = formData.rating === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() =>
                          setFormData((p) => ({ ...p, rating: opt.value }))
                        }
                        className={`h-11 rounded-xl text-xs sm:text-sm font-bold transition-all border ${
                          active
                            ? "bg-[#9b59bf] border-[#9b59bf] text-white shadow-sm shadow-purple-500/25"
                            : "bg-gray-50 border-gray-200 text-gray-600 hover:border-purple-200"
                        }`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Wilaya */}
              <div className="space-y-1.5 relative" data-wilaya-dropdown>
                <label className="flex items-center gap-1.5 text-xs font-bold text-gray-600">
                  <MapPin size={12} className="text-[#9b59bf]" />
                  {t("systemDhd.step1.state")}
                </label>

                <button
                  type="button"
                  onClick={() => setWilayaOpen((v) => !v)}
                  className="w-full h-11 rounded-xl px-3 text-sm font-semibold bg-gray-50 border border-gray-200 flex items-center justify-between gap-2 hover:border-purple-200 focus:outline-none focus:ring-2 focus:ring-purple-200"
                >
                  <span
                    className={
                      formData.state ? "text-gray-900" : "text-gray-400"
                    }
                  >
                    {formData.state || t("systemDhd.step1.statePlaceholder")}
                  </span>
                  <ChevronDown
                    size={16}
                    className={`text-gray-400 shrink-0 transition-transform ${
                      wilayaOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>

                {wilayaOpen && (
                  <div className="absolute z-[100] left-0 right-0 bottom-full mb-1.5 rounded-2xl border border-gray-200 bg-white shadow-xl overflow-hidden">
                    <div className="max-h-56 overflow-y-auto overscroll-contain py-1">
                      {wilayas.length === 0 ? (
                        <div className="px-3 py-4 text-center text-sm text-gray-400">
                          {t("systemDhd.step1.noWilaya", "لا توجد ولاية")}
                        </div>
                      ) : (
                        wilayas.map((w) => {
                          const selected =
                            formData.wilaya_code === String(w.wilaya_code);
                          return (
                            <button
                              key={String(w.wilaya_code)}
                              type="button"
                              onClick={() => {
                                setFormData((p) => ({
                                  ...p,
                                  state: w.name,
                                  wilaya_code: String(w.wilaya_code),
                                }));
                                setWilayaOpen(false);
                              }}
                              className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 text-sm text-start transition-colors ${
                                selected
                                  ? "bg-purple-50 text-[#9b59bf] font-bold"
                                  : "text-gray-700 hover:bg-gray-50"
                              }`}
                            >
                              <span>
                                <span className="text-gray-400 font-mono text-xs me-2">
                                  {w.wilaya_code}
                                </span>
                                {w.name}
                              </span>
                              {selected && (
                                <Check size={14} className="text-[#9b59bf]" />
                              )}
                            </button>
                          );
                        })
                      )}
                    </div>
                  </div>
                )}
              </div>

              <button
                type="button"
                onClick={() => canGoNext && setStep(2)}
                disabled={!canGoNext}
                className={`w-full h-12 rounded-2xl font-bold text-sm transition-all active:scale-[0.99] ${
                  canGoNext
                    ? "bg-[#9b59bf] hover:bg-[#814a9f] text-white shadow-md shadow-purple-500/25"
                    : "bg-gray-100 text-gray-400 cursor-not-allowed"
                }`}
              >
                {selectedCompanies.length === 0
                  ? t("systemDhd.step1.selectAtLeastOne")
                  : !formData.state
                    ? t("systemDhd.step1.statePlaceholder")
                    : t("systemDhd.step1.ctaReady")}
              </button>
            </div>
          )}

          {/* Step 2 */}
          {step === 2 && (
            <div className="text-center space-y-5 py-1">
              {hasDHD ? (
                <>
                  <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center">
                    <Check size={28} strokeWidth={2.5} />
                  </div>
                  <div>
                    <div className="text-xs font-bold tracking-wide text-[#9b59bf] mb-1">
                      {t("systemDhd.step2.withDhd.congrats")}
                    </div>
                    <h2 className="text-xl font-black text-gray-900">
                      {t("systemDhd.step2.withDhd.title")}
                    </h2>
                  </div>
                  <div className="rounded-2xl p-4 bg-purple-50 border border-purple-100 text-sm text-gray-700 leading-relaxed text-start">
                    <p>{t("systemDhd.step2.withDhd.descLine1")}</p>
                    <p className="mt-1">
                      {t("systemDhd.step2.withDhd.descLine2")}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setStep(3)}
                    className="w-full h-12 rounded-2xl font-bold text-sm text-white bg-[#9b59bf] hover:bg-[#814a9f] shadow-md shadow-purple-500/25 transition-all active:scale-[0.99]"
                  >
                    {t("systemDhd.step2.withDhd.waiting")}
                  </button>
                </>
              ) : (
                <>
                  <div className="w-16 h-16 mx-auto rounded-full bg-purple-100 text-[#9b59bf] flex items-center justify-center">
                    <Rocket size={26} />
                  </div>
                  <div>
                    <div className="text-xs font-bold tracking-wide text-[#9b59bf] mb-1">
                      {t("systemDhd.step2.noDhd.exclusive")}
                    </div>
                    <h2 className="text-xl font-black text-gray-900">
                      {t("systemDhd.step2.noDhd.title")}
                    </h2>
                  </div>

                  <div
                    className="rounded-2xl p-4 space-y-3 bg-gray-50 border border-gray-100 text-sm text-gray-700 leading-relaxed"
                    style={{ textAlign: isRTL ? "right" : "left" }}
                  >
                    <p>
                      {t("systemDhd.step2.noDhd.paragraph1.before")}{" "}
                      <span className="font-bold text-[#9b59bf]">
                        {t("systemDhd.step2.noDhd.paragraph1.highlight")}
                      </span>{" "}
                      {t("systemDhd.step2.noDhd.paragraph1.after")}
                    </p>
                    <div className="rounded-xl px-3 py-2.5 bg-white border border-purple-100 text-sm font-semibold text-gray-800">
                      {t("systemDhd.step2.noDhd.availableWith")}{" "}
                      <span className="text-[#9b59bf] font-black">DHD</span>{" "}
                      {t("systemDhd.step2.noDhd.only")}
                    </div>
                    <p>
                      {t("systemDhd.step2.noDhd.paragraph2.before")}{" "}
                      <span className="font-black text-[#9b59bf]">
                        {t("systemDhd.step2.noDhd.paragraph2.highlight")}
                      </span>
                    </p>
                  </div>

                  <div className="space-y-2.5">
                    <button
                      type="button"
                      onClick={() => {
                        setHasDHD(true);
                        setSelectedCompanies((prev) =>
                          prev.includes("DHD") ? prev : [...prev, "DHD"],
                        );
                        setStep(3);
                      }}
                      className="w-full h-12 rounded-2xl font-bold text-sm text-white bg-[#9b59bf] hover:bg-[#814a9f] shadow-md shadow-purple-500/25 transition-all active:scale-[0.99]"
                    >
                      {t("systemDhd.step2.noDhd.accept")}
                    </button>
                    <button
                      type="button"
                      onClick={handleClose}
                      className="w-full h-11 rounded-xl font-semibold text-sm text-gray-500 border border-gray-200 hover:bg-gray-50 transition-colors"
                    >
                      {t("systemDhd.step2.noDhd.noThanks")}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Step 3 */}
          {step === 3 && (
            <div
              className="space-y-5"
              style={{ textAlign: isRTL ? "right" : "left" }}
            >
              <div className="text-center">
                <div className="w-14 h-14 mx-auto rounded-2xl bg-purple-100 text-[#9b59bf] flex items-center justify-center mb-3">
                  <FileDown size={24} />
                </div>
                <h2 className="text-lg font-black text-gray-900">
                  {t("systemDhd.step3.title")}
                </h2>
              </div>

              <div className="rounded-2xl border border-gray-100 bg-gray-50 p-4 space-y-3">
                {[
                  t("systemDhd.step3.docs.idCard"),
                  t("systemDhd.step3.docs.form"),
                  t("systemDhd.step3.docs.businessRegister"),
                ].map((text, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-white border border-gray-200 flex items-center justify-center text-sm shrink-0">
                      {i === 0 ? "🆔" : i === 1 ? "📄" : "🏪"}
                    </div>
                    <span className="text-sm font-semibold text-gray-700 mt-1.5">
                      {text}
                    </span>
                  </div>
                ))}
              </div>

              <button
                type="button"
                onClick={handleFinish}
                disabled={saving}
                className={`w-full h-12 rounded-2xl font-bold text-sm text-white flex items-center justify-center gap-2 transition-all active:scale-[0.99] ${
                  saving
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-[#9b59bf] hover:bg-[#814a9f] shadow-md shadow-purple-500/25"
                }`}
              >
                {saving
                  ? t("systemDhd.step3.saving")
                  : t("systemDhd.step3.continue")}
              </button>
              <p className="text-[11px] text-center text-gray-400">
                {t("systemDhd.step3.noteNoDownload")}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
